import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE, FadeUp, MaskLines } from "./Reveal";
import LogoMarquee from "./LogoMarquee";

const TABS = [
  "Sell & Engage",
  "Package & Quote",
  "Book & Connect",
  "Operate & Service",
  "Run the Business",
];

const PRODUCTS = {
  "Sell & Engage": [
    { t: "Travel AI CRM", d: "Leads, follow-ups and conversions, guided by AI." },
    { t: "Call Centre Automation", d: "Route, record and resolve customer calls at scale." },
    { t: "Data Scraping + Bulk Email", d: "Build targeted lists and run outreach campaigns." },
  ],
  "Package & Quote": [
    { t: "Itinerary Builder", d: "Turn briefs into day-by-day itineraries in minutes." },
    { t: "Quoting & Proposals", d: "Professional quotes with live margins and approvals." },
    { t: "Dynamic Packaging", d: "Bundle stays, transfers and experiences in real time." },
  ],
  "Book & Connect": [
    { t: "Booking Engine", d: "Fast, conversion-first booking across every channel." },
    { t: "Supplier Connectivity", d: "One integration to hotels, flights and ground transport." },
    { t: "B2B Agent Portal", d: "Sub-agents book, track and settle in their own portal." },
  ],
  "Operate & Service": [
    { t: "Operations Command Centre", d: "Every booking, task and alert in one operations view." },
    { t: "Traveller Mobile App", d: "Documents, day plans and live support in travellers' pockets." },
    { t: "24/7 Support Desk", d: "AI-assisted queues that resolve issues before they escalate." },
  ],
  "Run the Business": [
    { t: "Finance & Reporting", d: "Reconciliations, payouts and P&L without spreadsheets." },
    { t: "Marketing Automation", d: "Campaigns and re-engagement that run themselves." },
    { t: "HR & Payroll", d: "People operations built for multi-country teams." },
  ],
};

export default function TravelTech() {
  const [tab, setTab] = useState(TABS[0]);

  return (
    <section id="travel-tech" className="scroll-mt-20 bg-paper py-24 text-[#0f0f14] md:py-28">
      <div className="mx-auto max-w-[1160px] px-6">
        <FadeUp>
          <p
            className="text-[13px] font-semibold uppercase tracking-[0.18em] text-brand"
            data-testid="travel-tech-eyebrow"
          >
            01 — Travel Technology
          </p>
        </FadeUp>

        <MaskLines
          lines={["Travel tech, built", "by travel experts."]}
          className="mt-8 text-[clamp(2.5rem,5.4vw,4.6rem)] font-normal leading-[1.04] tracking-[-0.025em]"
        />

        <FadeUp delay={0.1}>
          <p className="mt-8 max-w-[620px] text-[17px] leading-[1.65] text-[#54545c]">
            We run travel operations across six countries, so we build the tools we wished
            existed. Use them as ready SaaS, or let us custom-build for your workflow — at a
            fraction of typical enterprise cost.
          </p>
        </FadeUp>

        <FadeUp delay={0.15} className="mt-10 flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-2.5 rounded-full border border-black/[0.09] bg-white px-5 py-2.5 text-[15px] font-medium">
            <span className="h-2 w-2 rounded-full bg-brand" />
            SaaS — ready in days
          </span>
          <span className="inline-flex items-center gap-2.5 rounded-full border border-black/[0.09] bg-white px-5 py-2.5 text-[15px] font-medium">
            <span className="h-2 w-2 rounded-full bg-ink" />
            Custom build — your workflow, your brand
          </span>
        </FadeUp>

        <FadeUp delay={0.2} className="mt-12">
          <div role="tablist" aria-label="Product categories" className="flex flex-wrap gap-3">
            {TABS.map((label) => {
              const active = label === tab;
              return (
                <button
                  key={label}
                  role="tab"
                  aria-selected={active}
                  data-testid={`tab-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setTab(label)}
                  className={`relative rounded-full px-5 py-2.5 text-[15px] font-medium transition-colors duration-300 ${
                    active ? "text-white" : "border border-black/[0.09] bg-white text-[#0f0f14] hover:border-black/25"
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="tab-pill"
                      className="absolute inset-0 rounded-full bg-ink"
                      transition={{ duration: 0.45, ease: EASE }}
                    />
                  )}
                  <span className="relative z-10">{label}</span>
                </button>
              );
            })}
          </div>
        </FadeUp>

        <div className="mt-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35, ease: EASE }}
              className="grid gap-6 md:grid-cols-3"
              data-testid="product-cards-grid"
            >
              {PRODUCTS[tab].map((p) => (
                <a
                  key={p.t}
                  href="#contact"
                  data-testid={`product-card-${p.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex min-h-[212px] flex-col rounded-xl border border-black/[0.08] bg-white p-7 transition-all duration-300 hover:border-black/25 hover:shadow-[0_10px_30px_rgba(10,10,14,0.06)]"
                >
                  <h3 className="text-[22px] font-medium tracking-[-0.01em]">{p.t}</h3>
                  <p className="mt-2 max-w-[300px] text-[15px] leading-[1.6] text-[#54545c]">{p.d}</p>
                  <ArrowRight className="mt-auto self-end text-[#0f0f14]/70 transition-transform duration-300 group-hover:translate-x-1.5" />
                </a>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-20 border-t border-black/10 pt-10">
          <FadeUp>
            <p className="text-[19px] text-[#0f0f14]/85" data-testid="travel-tech-proof-line">
              Running today across Top 80+ travel management companies.
            </p>
          </FadeUp>
          <LogoMarquee />
        </div>
      </div>
    </section>
  );
}
